import type { Control } from "react-hook-form";
import { Box, Typography } from "@mui/material";
import { FormAccordionSection } from "Components/Shared/FormAccordionSection";
import { FormSection } from "Components/Shared/FormSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { CategoryAutocomplete } from "Components/Shared/Inputs/Form/CategoryAutocomplete";
import { OpeningHoursFormInput } from "Components/Shared/Inputs/Form/OpeningHoursFormInput";
import type { CategoryOption } from "Types/CategoryOption";
import type { FacilityFormModel } from "Components/Shared/Merchant/Facilities/schema";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;

type Props = {
  control: Control<FacilityFormModel>;
  isExpanded: boolean;
  onToggle: () => void;
  onNext: () => void;
  isPublic?: boolean | null;
  facilityCategoryOptions: Array<CategoryOption>;
  hasErrors: boolean;
};

export const PublicFacilityInfoSection = ({
  control,
  isExpanded,
  onToggle,
  onNext,
  isPublic,
  facilityCategoryOptions,
  hasErrors,
}: Props) => {
  const { t } = useResource();

  return (
    <FormAccordionSection
      legend={t(RESOURCES.Legend.PublicFacilityInfo)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      onNext={onNext}
      hasErrors={hasErrors}
    >
      <CheckboxInput
        control={control}
        name="isPublic"
        label={t(RESOURCES.Fields.IsPublic)}
      />
      {isPublic ? (
        <Box px={2}>
          <Typography my={2} variant="body1" color="text.secondary">
            {t(RESOURCES.PublicFacilityDescription)}
          </Typography>
          <CategoryAutocomplete
            control={control}
            name="categoryCodes"
            label={t(RESOURCES.Fields.Category)}
            categories={facilityCategoryOptions}
          />
          <FormInput
            control={control}
            name="description"
            label={t(RESOURCES.Fields.Description)}
            type="textarea"
            rows={4}
          />

          <FormSection legend={t(RESOURCES.Legend.DetailedOpeningHours)}>
            <OpeningHoursFormInput
              control={control}
              name="hours"
              label={t(RESOURCES.OpeningHours.Title)}
            />
          </FormSection>
        </Box>
      ) : (
        <Typography my={2} variant="body1" color="text.secondary">
          {t(RESOURCES.IsPublicHelpText)}
        </Typography>
      )}
    </FormAccordionSection>
  );
};
